import EditQuestion from "@/components/EditQuestion/EditQuestion";
import SelectQuestionsDialog from "@/components/SelectQuestions/SelectQuestionsDialog";
import {
    Button,
    Card,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoCourseQuiz } from "@/shared/model/edo-course-quiz.model";
import { IEdoQuestion } from "@/shared/model/edo-question.model";
import { IEdoQuizQuestion } from "@/shared/model/edo-quiz-question.model";
import { EdoQuestionTypeEnum } from "@/shared/model/enumerations/edo-question-type.mode";
import { getEntity } from "@/shared/reducers/entities/edo-course-quiz.reducer";
import { createEntity as createQuizQuestion } from "@/shared/reducers/entities/edo-quiz-question.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { AlignLeft, CheckSquare, CircleDot, Plus, ToggleLeft } from "lucide-react";
import axios from "axios";
import { useEffect, useState } from "react";
import { Translate } from "react-jhipster";
import { useParams } from "react-router-dom";
import CourseQuestion from "./components/CourseQuestion";
import RemoveQuestion from "./components/RemoveQuestion";

const QuizDetailRoute = () => {
    const dispatch = useAppDispatch();
    const params = useParams<{ id: string }>();
    const { entity: courseQuiz } = useAppSelector<EntityState<IEdoCourseQuiz>>(
        (state) => state.edoCourseQuiz
    );
    const { updateSuccess: updateSuccessQuizQuestion } = useAppSelector<
        EntityState<IEdoQuizQuestion>
    >((state) => state.edoQuizQuestion);
    const [questions, setQuestions] = useState<IEdoQuizQuestion[]>([]);
    const [editQuestionDialog, setEditQuestionDialog] = useState<{
        open: boolean;
        entity: IEdoQuestion | null;
        type: EdoQuestionTypeEnum | null;
    }>({ open: false, entity: null, type: null });
    const [questionsToAdd, setQuestionsToAdd] = useState<IEdoQuestion[]>([]);
    const [selectQuestionsDialog, setSelectQuestionsDialog] = useState<{
        open: boolean;
    }>({ open: false });
    const [removeQuestionDialog, setRemoveQuestionDialog] = useState<{
        open: boolean;
        entity: IEdoQuizQuestion | null;
    }>({ open: false, entity: null });

    const getQuizQuestions = async (quizId: number) => {
        const response = await axios.get<{ data: IEdoQuizQuestion[] }>(
            `/quizzes/${quizId}/questions`,
            { params: { sort: "updatedAt,ASC" } }
        );
        setQuestions(response.data.data);
    };

    const handleQuestionCreated = async () => {
        getQuizQuestions(courseQuiz?.quiz?.id);
    };

    const handleAddQuestionsToQuiz = async (newQuestions: IEdoQuestion[]) => {
        await Promise.all(
            newQuestions.map((question) =>
                dispatch(
                    createQuizQuestion({
                        quiz: courseQuiz.quiz,
                        question,
                    })
                )
            )
        );
        setQuestionsToAdd([]);
        getQuizQuestions(courseQuiz?.quiz?.id);
    };

    useEffect(() => {
        dispatch(getEntity(params.id));
    }, [dispatch, params.id]);

    useEffect(() => {
        if (courseQuiz?.quiz?.id) {
            getQuizQuestions(courseQuiz.quiz.id);
        }
    }, [courseQuiz]);

    useEffect(() => {
        if (updateSuccessQuizQuestion && courseQuiz?.quiz?.id) {
            getQuizQuestions(courseQuiz.quiz.id);
        }
    }, [updateSuccessQuizQuestion, courseQuiz?.quiz?.id]);

    const menuItems = [
        {
            type: EdoQuestionTypeEnum.SINGLE_CHOICE,
            label: "Crea domanda a risposta singola",
            icon: <CircleDot className="h-4 w-4" />,
        },
        {
            type: EdoQuestionTypeEnum.MULTIPLE_CHOICE,
            label: "Crea domanda a risposta multipla",
            icon: <CheckSquare className="h-4 w-4" />,
        },
        {
            type: EdoQuestionTypeEnum.TRUE_FALSE,
            label: "Crea domanda Vero/Falso",
            icon: <ToggleLeft className="h-4 w-4" />,
        },
        {
            type: EdoQuestionTypeEnum.FREE_TEXT,
            label: "Crea domanda a risposta scritta",
            icon: <AlignLeft className="h-4 w-4" />,
        },
    ] as const;

    return (
        <>
            <div className="space-y-6">
                <Card className="surface overflow-hidden">
                    <div className="p-6">
                        <h2 className="mb-2 text-heading-2 font-semibold tracking-tight text-[var(--color-mockup-text)]">
                            {courseQuiz?.quiz?.title}
                        </h2>
                        <p className="text-body text-[var(--color-mockup-text-mid)]">
                            {courseQuiz?.quiz?.description}
                        </p>
                    </div>
                </Card>

                <div className="flex flex-wrap justify-end gap-2 sm:mb-4">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="primary"
                                className="inline-flex items-center gap-2 transition-all duration-200 hover:scale-[1.02]"
                            >
                                <Plus className="h-5 w-5" />
                                Aggiungi domanda
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[220px]">
                            {menuItems.map((item) => (
                                <DropdownMenuItem
                                    key={item.type}
                                    onClick={() =>
                                        setEditQuestionDialog({
                                            open: true,
                                            entity: null,
                                            type: item.type,
                                        })
                                    }
                                >
                                    {item.icon}
                                    {item.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="space-y-6">
                    {questions.length ? (
                        questions.map((quizQuestion) => (
                            <CourseQuestion
                                key={quizQuestion.id}
                                onEdit={(question) =>
                                    setEditQuestionDialog({
                                        open: true,
                                        entity: question,
                                        type: null,
                                    })
                                }
                                onDelete={() =>
                                    setRemoveQuestionDialog({
                                        open: true,
                                        entity: quizQuestion,
                                    })
                                }
                                className="mb-6"
                                question={quizQuestion.question}
                            />
                        ))
                    ) : (
                        <Card className="surface p-6">
                            <p className="text-center text-body text-[var(--color-mockup-text-mid)]">
                                <Translate contentKey="edocendoApp.edoQuestion.home.notFound" />
                            </p>
                        </Card>
                    )}
                </div>
            </div>

            <EditQuestion
                open={editQuestionDialog.open}
                question={editQuestionDialog.entity}
                onClose={() =>
                    setEditQuestionDialog({ open: false, entity: null, type: null })
                }
                onSuccess={handleQuestionCreated}
                type={editQuestionDialog.type}
                quiz={courseQuiz?.quiz}
            />
            <SelectQuestionsDialog
                open={selectQuestionsDialog.open}
                onSuccess={handleAddQuestionsToQuiz}
                onClose={() => setSelectQuestionsDialog({ open: false })}
                selectedQuestions={questionsToAdd}
                setSelectedQuestions={(q) => setQuestionsToAdd(q)}
            />
            <RemoveQuestion
                open={removeQuestionDialog.open}
                question={removeQuestionDialog.entity}
                onClose={() =>
                    setRemoveQuestionDialog({ open: false, entity: null })
                }
            />
        </>
    );
};

export default QuizDetailRoute;
